import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, TrendingUp, ArrowUpRight, BarChart3 } from "lucide-react";
import { Annotation } from "@/components/Annotation";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar,
  Legend
} from "recharts";
import { useDateRange } from "@/components/DateRangeFilter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return Math.round(val);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
      <p className="text-slate-300 font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-mono">
          {p.name}: INR {Number(p.value).toLocaleString()}
        </p>
      ))}
    </div>
  );
};

export default function RevenueExpansion() {
  const [monthly, setMonthly] = useState([]);
  const [expansion, setExpansion] = useState([]);
  const [loading, setLoading] = useState(true);
  const { filterData } = useDateRange();

  useEffect(() => {
    axios.get(`${API}/revenue/expansion`).then((res) => {
      setMonthly(res.data.monthly || []);
      setExpansion(res.data.expansion || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filteredMonthly = useMemo(() => filterData(monthly, "month"), [monthly, filterData]);
  const filteredExpansion = useMemo(() => filterData(expansion, "cohort"), [expansion, filterData]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 skeleton-shimmer rounded" />
        <div className="h-[300px] skeleton-shimmer rounded" />
      </div>
    );
  }

  const totalRevenue = filteredMonthly.reduce((s, m) => s + (m.total_revenue || 0), 0);
  const returningRevenue = filteredMonthly.reduce((s, m) => s + (m.returning_revenue || 0), 0);
  const expansionPct = totalRevenue > 0 ? ((returningRevenue / totalRevenue) * 100).toFixed(1) : 0;

  // Top expanding cohorts
  const topExpansion = [...filteredExpansion].sort((a, b) => b.expansion_rate - a.expansion_rate).slice(0, 10);

  return (
    <div className="space-y-6" data-testid="revenue-expansion-page">
      <div>
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">
          Revenue Expansion
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          New vs returning customer revenue &middot; Cohort expansion rates
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="kpi-card border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="total-revenue-metric">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Total Revenue</span>
              <DollarSign className="w-4 h-4 text-emerald-400" />
            </div>
            <p className="font-space text-2xl font-bold tracking-tight text-emerald-400">
              INR {formatINR(totalRevenue)}
            </p>
          </CardContent>
        </Card>

        <Card className="kpi-card border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="returning-revenue-metric">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Returning Revenue</span>
              <TrendingUp className="w-4 h-4 text-blue-400" />
            </div>
            <p className="font-space text-2xl font-bold tracking-tight text-blue-400">
              INR {formatINR(returningRevenue)}
            </p>
          </CardContent>
        </Card>

        <Card className="kpi-card border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="expansion-share-metric">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Returning Share</span>
              <ArrowUpRight className="w-4 h-4 text-violet-400" />
            </div>
            <p className="font-space text-2xl font-bold tracking-tight text-violet-400">
              {expansionPct}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Stacked Area Chart */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="revenue-stacked-chart">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-400" />
            Monthly Revenue: New vs Returning Customers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={filteredMonthly}>
              <defs>
                <linearGradient id="newGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} interval={2} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={formatINR} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="new_customer_revenue" stackId="1" stroke="#3b82f6" fill="url(#newGrad)" strokeWidth={1.5} name="New Customer" />
              <Area type="monotone" dataKey="returning_revenue" stackId="1" stroke="#10b981" fill="url(#retGrad)" strokeWidth={1.5} name="Returning" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-6 mt-3 text-xs text-slate-400">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-blue-500" />
              <span>New Customer Revenue</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-emerald-500" />
              <span>Returning Customer Revenue</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Annotation>
        The green layer (Returning Revenue) is generated at zero incremental acquisition cost — these are customers who already trusted the brand and returned organically. The {expansionPct}% returning share is a <strong className="text-amber-300">conservative floor</strong>: customers who repurchase on Amazon/Flipkart instead of our website (common during sales when marketplace pricing is subsidized) are not captured here. Additionally, customers who create new website accounts for first-time discount coupons appear as "new" revenue rather than "returning." The true returning revenue contribution across all channels is materially higher.
      </Annotation>

      {/* Cohort Expansion Table */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="cohort-expansion-table">
        <CardHeader className="pb-3">
          <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            Cohort Expansion Rates (Top Performers)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Cohort</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Size</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">M0 Revenue</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Expansion Revenue</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Expansion Rate</th>
                </tr>
              </thead>
              <tbody>
                {topExpansion.map((e) => (
                  <tr key={e.cohort} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                    <td className="py-2.5 px-3 font-mono text-slate-300">{e.cohort}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-400">{e.cohort_size?.toLocaleString()}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-300">{formatINR(e.month_0_revenue)}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-emerald-400">{formatINR(e.expansion_revenue)}</td>
                    <td className="py-2.5 px-3 text-right">
                      <Badge
                        variant="outline"
                        className={`font-mono text-[10px] ${
                          e.expansion_rate > 10 ? "text-emerald-400 border-emerald-800" :
                          e.expansion_rate > 5 ? "text-blue-400 border-blue-800" :
                          "text-slate-400 border-slate-700"
                        }`}
                      >
                        +{e.expansion_rate}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
